import { useCallback, useEffect, useRef, useState } from "react";
import { Search, Loader2, Play, Copy, AlertTriangle, Info, ChevronDown, ChevronUp, History, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDT } from "@/constants";
import { Button } from "@/components/ui/button";

const CATS = {
  urgent: { label: "Urgent", cls: "bg-rose-100 text-rose-700 border-rose-300" },
  important: { label: "Important", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  amelioration: { label: "Amélioration", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  information: { label: "Information", cls: "bg-slate-100 text-slate-600 border-slate-300" },
};

const FINDING_STATUS = [
  ["a_traiter", "À traiter"],
  ["en_cours", "En cours"],
  ["corrigee", "Corrigée"],
  ["ignoree", "Ignorée"],
];

const LLM_LABELS = {
  ok: "Analyse OpenAI terminée",
  cle_absente: "API OpenAI non configurée — constats techniques uniquement",
  cle_invalide: "Clé API OpenAI invalide — constats techniques uniquement",
  modele_indisponible: "Modèle OpenAI indisponible sur ce compte — constats techniques uniquement",
  erreur: "Erreur OpenAI — constats techniques uniquement",
  erreur_parse: "Réponse OpenAI illisible — constats techniques uniquement",
  non_lance: "Analyse OpenAI non lancée",
};

function CopyBtn({ text, testid }) {
  if (!text) return null;
  return (
    <button
      data-testid={testid}
      onClick={() => { navigator.clipboard?.writeText(text); toast.success("Proposition copiée — collez-la dans Webador"); }}
      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-sky-700 hover:border-sky-400 transition-colors"
    >
      <Copy className="h-3.5 w-3.5" /> Copier
    </button>
  );
}

function FindingCard({ f, auditId, onSaved, index }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(f.note || "");
  const cat = CATS[f.category] || CATS.information;
  const save = (patch) => {
    api.patch(`/seo/audits/${auditId}/findings/${f.id}`, patch)
      .then(() => onSaved())
      .catch(() => toast.error("Enregistrement impossible"));
  };
  return (
    <div className={`rounded-2xl border bg-white overflow-hidden ${cat.cls.split(" ").pop()}`} data-testid={`seo-finding-${index}`}>
      <button type="button" className="w-full text-left p-3.5 flex items-start gap-2.5 hover:bg-slate-50 transition-colors"
        data-testid={`seo-finding-toggle-${index}`} onClick={() => setOpen(!open)}>
        <span className={`shrink-0 mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cat.cls}`}>{cat.label}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">{f.problem}</span>
          {f.page && (
            <a href={f.page} target="_blank" rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`seo-page-link-${index}`}
              className="block text-xs text-sky-600 hover:underline truncate">{f.page}</a>
          )}
        </span>
        <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded ${f.source === "analyse OpenAI" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-500"}`}>
          {f.source === "analyse OpenAI" ? "IA" : "Auto"}
        </span>
        {f.proposal && (
          <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" data-testid={`seo-ready-${index}`}>
            Prêt à copier
          </span>
        )}
        {open ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
      </button>
      {open && (
        <div className="border-t border-slate-100 p-4 space-y-3 text-sm" data-testid={`seo-finding-detail-${index}`}>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Explication</p>
            <p className="text-slate-700">{f.explanation}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Impact SEO probable</p>
            <p className="text-slate-700">{f.impact}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Action recommandée</p>
            {(f.action_kind || f.bloc) && (
              <div className="flex gap-1.5 flex-wrap my-1">
                {f.action_kind && (
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${f.action_kind === "ajout" ? "bg-sky-100 text-sky-700" : f.action_kind === "remplacement" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"}`}
                    data-testid={`seo-action-kind-${index}`}>
                    {{ ajout: "Ajout", remplacement: "Remplacement", enrichissement: "Enrichissement" }[f.action_kind] || f.action_kind}
                  </span>
                )}
                {f.bloc && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid={`seo-bloc-${index}`}>
                    Bloc : {f.bloc}
                  </span>
                )}
              </div>
            )}
            <p className="text-slate-700">{f.action}</p>
            {(f.words_current != null || f.words_proposal != null) && (
              <p className="text-xs text-slate-500 font-mono tabular-nums mt-1" data-testid={`seo-wordcount-${index}`}>
                {f.words_current != null && `Contenu actuel : ${f.words_current} mots (contenu principal, menus exclus)`}
                {f.words_current != null && f.words_proposal != null && " · "}
                {f.words_proposal != null && `Proposition : ${f.words_proposal} mots (même méthode de calcul)`}
              </p>
            )}
            {f.justification && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-1" data-testid={`seo-justification-${index}`}>
                Justification du remplacement : {f.justification}
              </p>
            )}
          </div>
          {(f.current || f.proposal) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {f.current && (
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Contenu actuel</p>
                  <p className="text-xs text-slate-600 whitespace-pre-wrap">{f.current}</p>
                </div>
              )}
              {f.proposal && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">Proposition prête à copier</p>
                  <p className="text-xs text-emerald-900 whitespace-pre-wrap" data-testid={`seo-proposal-${index}`}>{f.proposal}</p>
                  <CopyBtn text={f.proposal} testid={`seo-copy-${index}`} />
                </div>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <select
              data-testid={`seo-status-select-${index}`}
              value={f.status}
              onChange={(e) => save({ status: e.target.value })}
              className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs font-semibold"
            >
              {FINDING_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input
              data-testid={`seo-note-${index}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={() => { if (note !== (f.note || "")) save({ note }); }}
              placeholder="Note manuelle…"
              className="flex-1 min-w-[180px] h-9 rounded-lg border border-slate-200 px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-sky-400"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function Seo() {
  const [status, setStatus] = useState(null);
  const [audits, setAudits] = useState([]);
  const [audit, setAudit] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [fCat, setFCat] = useState("tous");
  const [fPage, setFPage] = useState("toutes");
  const [fType, setFType] = useState("tous");
  const [fReady, setFReady] = useState(false);
  const pollRef = useRef(null);

  const loadAudit = useCallback((id) => {
    api.get(`/seo/audits/${id}`).then((r) => setAudit(r.data)).catch(() => toast.error("Audit introuvable"));
  }, []);

  const load = useCallback(() => {
    api.get("/seo/status").then((r) => {
      setStatus(r.data);
      if (r.data.last_audit && !audit) loadAudit(r.data.last_audit.id);
    }).catch(() => toast.error("Chargement impossible"));
    api.get("/seo/audits").then((r) => setAudits(r.data)).catch(() => {});
  }, [audit, loadAudit]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    if (status?.running) {
      pollRef.current = setInterval(() => {
        api.get("/seo/status").then((r) => {
          setStatus(r.data);
          if (!r.data.running) {
            clearInterval(pollRef.current);
            if (r.data.last_audit) loadAudit(r.data.last_audit.id);
            api.get("/seo/audits").then((x) => setAudits(x.data));
          }
        }).catch(() => {});
      }, 2500);
      return () => clearInterval(pollRef.current);
    }
  }, [status?.running, loadAudit]);

  const launch = async () => {
    setLaunching(true);
    try {
      await api.post("/seo/audit", { confirm: true });
      setConfirm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lancement impossible");
    } finally {
      setLaunching(false);
    }
  };

  const findings = (audit?.findings || []).filter((f) =>
    (fCat === "tous" || f.category === fCat) &&
    (fType === "tous" || f.type === fType) &&
    (!fReady || f.proposal) &&
    (fPage === "toutes" || (f.page || "").includes(fPage)));
  const readyCount = (audit?.findings || []).filter((f) => f.proposal).length;
  const pages = [...new Set((audit?.findings || []).map((f) => f.page).filter(Boolean))];
  const types = [...new Set((audit?.findings || []).map((f) => f.type))];
  const counts = Object.keys(CATS).map((c) => [c, (audit?.findings || []).filter((f) => f.category === c).length]);

  return (
    <div className="space-y-5" data-testid="seo-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            <Search className="h-6 w-6 text-slate-500" /> Assistant SEO
          </h1>
          <p className="text-sm text-slate-500">Audit manuel du site public photobooth54.fr — constats et propositions prêtes pour Webador.</p>
        </div>
        <span
          data-testid="seo-key-status"
          className={`rounded-full px-3 py-1.5 text-xs font-bold ${status?.key_configured ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
        >
          {status?.key_configured ? "API OpenAI configurée" : "API OpenAI non configurée"} · modèle {status?.model || "…"}
        </span>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="seo-key-card">
        {status?.key_configured ? (
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-sm font-semibold text-emerald-700" data-testid="seo-key-ok">
              API OpenAI configurée ({status.key_hint}) — la clé est chiffrée côté serveur.
            </p>
            <Button variant="outline" size="sm" data-testid="seo-key-delete" onClick={async () => {
              try { await api.delete("/seo/config/openai-key"); toast.success("Clé supprimée"); load(); }
              catch { toast.error("Suppression impossible"); }
            }}>Supprimer la clé</Button>
          </div>
        ) : (
          <form className="space-y-2" onSubmit={async (e) => {
            e.preventDefault();
            const input = e.target.elements.key;
            setLaunching(true);
            try {
              await api.post("/seo/config/openai-key", { key: input.value });
              toast.success("Clé OpenAI vérifiée et enregistrée (chiffrée)");
              input.value = "";
              load();
            } catch (err) {
              toast.error(err.response?.data?.detail || "Enregistrement impossible");
            } finally { setLaunching(false); }
          }}>
            <p className="text-sm font-bold">Clé API OpenAI</p>
            <p className="text-xs text-slate-500">
              Collez votre clé (sk-…) depuis platform.openai.com/api-keys : elle est vérifiée auprès d'OpenAI puis
              chiffrée côté serveur. Jamais affichée, jamais stockée en clair, jamais dans le code ni les journaux.
            </p>
            <div className="flex gap-2">
              <input name="key" type="password" required autoComplete="off" placeholder="sk-…"
                data-testid="seo-key-input"
                className="flex-1 h-11 rounded-xl border border-slate-200 px-3.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sky-400" />
              <Button type="submit" data-testid="seo-key-save" disabled={launching}
                className="bg-slate-900 hover:bg-slate-800 text-white transition-colors">
                {launching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
              </Button>
            </div>
          </form>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-3" data-testid="seo-launch-card">
        <div className="text-sm text-slate-600 space-y-1">
          <p>Dernier audit : <strong data-testid="seo-last-audit">{status?.last_audit?.started_at ? fmtDT(status.last_audit.started_at) : "jamais"}</strong></p>
          <p>Pages à examiner : <strong data-testid="seo-pages-estimate">{status?.pages_estimate ? `≈ ${status.pages_estimate}` : "estimation après 1re exploration (max 30)"}</strong></p>
          <p className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            L'audit parcourt le site public (environ 1 requête/seconde) puis, si la clé est configurée, effectue un appel à l'API OpenAI facturé sur votre compte OpenAI. Sans clé, seuls les constats techniques sont produits.
          </p>
        </div>
        {status?.running ? (
          <div className="flex items-center gap-2 text-sm font-semibold text-sky-700" data-testid="seo-progress">
            <Loader2 className="h-4 w-4 animate-spin" /> Audit en cours… {status.running.progress_pages || 0} page(s) explorée(s)
          </div>
        ) : !confirm ? (
          <Button data-testid="seo-launch-button" onClick={() => setConfirm(true)}
            className="bg-sky-500 hover:bg-sky-600 text-white transition-colors">
            <Play className="h-4 w-4" /> Lancer un audit complet
          </Button>
        ) : (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 p-3" data-testid="seo-confirm-box">
            <p className="text-sm font-semibold text-sky-900 w-full">Confirmer le lancement de l'audit du site public ?</p>
            <Button size="sm" data-testid="seo-confirm-launch" disabled={launching} onClick={launch}
              className="bg-sky-500 hover:bg-sky-600 text-white transition-colors">
              {launching && <Loader2 className="h-4 w-4 animate-spin" />} Oui, lancer l'audit
            </Button>
            <Button size="sm" variant="outline" data-testid="seo-cancel-launch" onClick={() => setConfirm(false)}>Annuler</Button>
          </div>
        )}
      </section>

      {audit && audit.status === "done" && (
        <>
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="seo-summary">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-xs text-slate-500">Note globale</p>
              <p className={`text-3xl font-extrabold font-heading ${audit.score >= 80 ? "text-emerald-600" : audit.score >= 50 ? "text-amber-600" : "text-rose-600"}`} data-testid="seo-score">{audit.score}/100</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-xs text-slate-500">Pages analysées</p>
              <p className="text-3xl font-extrabold font-heading" data-testid="seo-pages-count">{audit.pages_count}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-xs text-slate-500">Urgent / Important</p>
              <p className="text-3xl font-extrabold font-heading text-rose-600">{counts[0][1]} <span className="text-amber-600 text-xl">/ {counts[1][1]}</span></p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-xs text-slate-500">Recommandations</p>
              <p className="text-3xl font-extrabold font-heading text-sky-700" data-testid="seo-findings-count">{audit.findings.length}</p>
            </div>
          </section>
          <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="seo-llm-status">
            <Sparkles className="h-3.5 w-3.5" /> {LLM_LABELS[audit.llm_status] || audit.llm_status}
            {audit.usage?.cost_estimate_usd != null && ` — ${audit.usage.input_tokens + audit.usage.output_tokens} tokens (≈ ${audit.usage.cost_estimate_usd} $)`}
          </p>
          {audit.summary && (
            <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="seo-ai-summary">
              <h2 className="font-heading font-bold mb-2">État général (analyse IA)</h2>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{audit.summary}</p>
            </section>
          )}

          {(audit.opportunities || []).length > 0 && (
            <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 sm:p-5" data-testid="seo-opportunities">
              <h2 className="font-heading font-bold mb-2">Opportunités de contenus locaux</h2>
              <ul className="space-y-2">
                {audit.opportunities.map((o, i) => (
                  <li key={i} className="text-sm text-emerald-900" data-testid={`seo-opportunity-${i}`}>
                    <strong>{o.title}</strong>{o.description ? ` — ${o.description}` : ""}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="space-y-3">
            <div className="flex flex-wrap gap-1.5" data-testid="seo-filters">
              <button data-testid="seo-filter-cat-tous" onClick={() => setFCat("tous")}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${fCat === "tous" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-600"}`}>
                Tous ({audit.findings.length})
              </button>
              <button data-testid="seo-filter-ready" onClick={() => setFReady(!fReady)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${fReady ? "bg-emerald-600 text-white border-emerald-600" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}>
                Prêtes à copier ({readyCount})
              </button>
              {Object.entries(CATS).map(([k, v]) => (
                <button key={k} data-testid={`seo-filter-cat-${k}`} onClick={() => setFCat(k)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${fCat === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-600"}`}>
                  {v.label} ({counts.find(([c]) => c === k)[1]})
                </button>
              ))}
              <select data-testid="seo-filter-page" value={fPage} onChange={(e) => setFPage(e.target.value)}
                className="h-8 rounded-full border border-slate-200 bg-white px-2 text-xs font-semibold max-w-[220px]">
                <option value="toutes">Toutes les pages</option>
                {pages.map((p) => <option key={p} value={p}>{p.replace("https://www.photobooth54.fr", "") || "/"}</option>)}
              </select>
              <select data-testid="seo-filter-type" value={fType} onChange={(e) => setFType(e.target.value)}
                className="h-8 rounded-full border border-slate-200 bg-white px-2 text-xs font-semibold">
                <option value="tous">Tous les types</option>
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {findings.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="seo-no-findings">
                Aucun constat pour ces filtres.
              </p>
            ) : (
              <div className="space-y-2" data-testid="seo-findings-list">
                {findings.map((f, i) => <FindingCard key={f.id} f={f} index={i} auditId={audit.id} onSaved={() => loadAudit(audit.id)} />)}
              </div>
            )}
          </section>

          {audit.recap && (
            <section className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="seo-recap">
              <div className="rounded-2xl border border-rose-200 bg-white p-4">
                <h3 className="font-heading font-bold text-rose-700 text-sm mb-2">Pages nécessitant le plus de travail</h3>
                <ul className="space-y-1.5">
                  {audit.recap.a_travailler.map((p, i) => (
                    <li key={i} className="text-xs" data-testid={`seo-recap-work-${i}`}>
                      <span className="font-semibold">{p.url.replace("https://www.photobooth54.fr", "") || "/"}</span>
                      <span className="text-slate-500"> — {p.issues} point(s), {p.words} mots</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-emerald-200 bg-white p-4">
                <h3 className="font-heading font-bold text-emerald-700 text-sm mb-2">Pages les mieux optimisées</h3>
                <ul className="space-y-1.5">
                  {audit.recap.mieux_notees.map((p, i) => (
                    <li key={i} className="text-xs" data-testid={`seo-recap-best-${i}`}>
                      <span className="font-semibold">{p.url.replace("https://www.photobooth54.fr", "") || "/"}</span>
                      <span className="text-slate-500"> — {p.issues} point(s), {p.words} mots</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}
        </>
      )}

      {audit && audit.status === "error" && (
        <p className="rounded-2xl border border-rose-300 bg-rose-50 p-4 text-sm text-rose-700" data-testid="seo-error">
          L'audit a échoué : {audit.error || "erreur inconnue"}. Aucune donnée n'a été modifiée — vous pouvez relancer.
        </p>
      )}

      {audits.length > 0 && (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="seo-history">
          <h2 className="font-heading font-bold mb-2 flex items-center gap-2"><History className="h-4 w-4 text-slate-400" /> Historique des audits</h2>
          <ul className="space-y-1.5">
            {audits.map((a, i) => (
              <li key={a.id}>
                <button data-testid={`seo-history-${i}`} onClick={() => loadAudit(a.id)}
                  className={`w-full text-left rounded-xl border px-3 py-2 text-sm hover:border-sky-300 transition-colors flex items-center gap-2 flex-wrap ${audit?.id === a.id ? "border-sky-400 bg-sky-50" : "border-slate-200"}`}>
                  <span className="font-medium">{fmtDT(a.started_at)}</span>
                  <span className="text-xs text-slate-500">{a.status === "done" ? `${a.pages_count} pages · note ${a.score}/100` : a.status}</span>
                  {a.usage?.cost_estimate_usd != null && <span className="text-xs text-slate-400 ml-auto">≈ {a.usage.cost_estimate_usd} $</span>}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="text-[11px] text-slate-400 flex items-center gap-1">
        <Info className="h-3 w-3" /> Audit 100 % manuel — aucune tâche automatique. Phase 2 prévue : Search Console, audit hebdomadaire.
      </p>
    </div>
  );
}
